import { LitElement, html, nothing } from 'lit';
import fsTypoStyles from '@cof/fs-typography-core/lib/typography.lit';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import fsButton from '@cof/fs-button-core/lib/button.lit.min.js';
import { DebugService } from '../../../shared/services/debug.service.js';
import { style } from './step-iconic-income.style.js';
import { globalStyle } from '../../../global-styles.style.js';
import { ApplicationService } from '../../../services/application.service.js';
import { REFI_PERSONAL_CONST } from '../../../shared/constants/app.constants.js';
import { stratum } from '../../../../constants/stratum.js';
import { validateForm } from '../../../shared/decorators/validate-form.decorator.js';
import '../step-income/job/job.component.js';
import '../step-income/income/income.component.js';

class RefiStepIconicIncomeComponent extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiStepIconicIncome');
    this.application = {};
    this.validity = {};
    this.isIconic = false;
    this.employmentMissing = false;
    this.showAdditionalIncome = false;
    this.childIds = {
      job: 'job-input',
      income: 'income-input',
      additionalIncome: 'additional-income-input',
      employmentActiveToggle: 'employment-active-toggle-input',
    };
  }

  static properties = {
    application: { type: Object, reflect: true },
    applicant: { type: Object, reflect: true },
    isIconic: { type: Boolean, attribute: 'is-iconic', reflect: true },
    employmentStatus: { type: String, reflect: true },
    employedOrSelfEmployed: { type: Boolean, reflect: true },
    isPrefill: { type: Boolean, attribute: 'is-prefill' },
    isPrimary: { type: Boolean, attribute: 'is-primary', reflect: true },
    showAdditionalIncome: { type: Boolean, reflect: true },
  };

  static styles = [style, globalStyle, fsTypoStyles, fsButton];

  connectedCallback() {
    super.connectedCallback();
    this.application = ApplicationService.Application;
    this.applicant = this.application.primaryApplicant;
    this.employmentStatus = this.applicant.employmentStatus;
    this.employedOrSelfEmployed =
      this.employmentStatus ===
        REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.EMPLOYED ||
      this.employmentStatus ===
        REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.SELF_EMPLOYED;
    this.showAdditionalIncome = !!this.applicant.otherIncome ?? false;
    stratum.publishTag(
      this.isPrimary
        ? TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_PERSONAL_EMPLOYMENT
        : TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_PERSONAL_CO_APP_EMPLOYMENT
    );
    stratum.publishTag(
      this.isPrimary
        ? TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_PERSONAL_INCOME
        : TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_PERSONAL_CO_APP_INCOME
    );
  }

  render() {
    // Comment for "Add another source of income" button:
    // Cannot use a fs-button here because as of right now,
    // there is no way to use the aria-expanded attribute on the FsButton web component
    // Issue link: https://jira.kdc.capitalone.com/browse/XDA-2291
    return html`
      <form
        id="step-iconic-income-form"
        class="step-iconic-income-form"
        @submit="${this.onSubmit}"
        novalidate
      >
        <refi-heading is-iconic> Tell us about your job </refi-heading>
        <fs-select
          id="job-status"
          name="job-status-select"
          required
          value-missing-message="Please select a job status"
          @change="${this.employmentChange}"
          value="${this.employmentStatus}"
        >
          <span slot="label">Job status</span>
          <option value="">Select a job status</option>
          <option value="${REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.EMPLOYED}">
            Employed
          </option>
          <option value="${REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.UNEMPLOYED}">
            Unemployed
          </option>
          <option
            value="${REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.SELF_EMPLOYED}"
          >
            Self-Employed/1099 contractor
          </option>
          <option value="${REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.RETIRED}">
            Retired
          </option>
          <option value="${REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.OTHER}">
            Other
          </option>
        </fs-select>

        <fs-body-text
          size="small"
          variant="feedback-negative"
          ?hidden="${!this.employmentMissing}"
          id="employmentActiveInvalid"
        >
          Please select an employment status.
        </fs-body-text>

        ${this.employedOrSelfEmployed
          ? html` <refi-personal-job
              id="${this.childIds.job}"
              @validity="${this.updateValidity}"
              ?is-primary="${this.isPrimary}"
            ></refi-personal-job>`
          : nothing}
        ${this.employedOrSelfEmployed
          ? html` <refi-personal-employment-active-toggle
              id="${this.childIds.employmentActiveToggle}"
              @validity="${this.updateValidity}"
              ?is-primary="${this.isPrimary}"
              is-iconic
            ></refi-personal-employment-active-toggle>`
          : nothing}

        <refi-personal-income
          id="${this.childIds.income}"
          @validity="${this.updateValidity}"
          ?is-primary="${this.isPrimary}"
          is-iconic
          helper-text="You don’t have to include alimony, child support, 
            or separate maintenance income unless you want to use it to repay your loan."
        ></refi-personal-income>

        <div class="button-container-iconic">
          <button
            class="step-income__add-additional-source fs-button fs-button--primary fs-button--compact fs-button--ghost"
            aria-expanded="${this.showAdditionalIncome}"
            @click="${(e) => {
              this.toggleAdditionalIncome(e);
            }}"
            @keydown="${(e) => {
              if (e.key === 'Enter') {
                this.toggleAdditionalIncome(e);
              }
            }}"
          >
            ${this.showAdditionalIncome
              ? html`Remove another source of income`
              : html`Add another source of income`}
          </button>
        </div>
        ${this.showAdditionalIncome
          ? html` <refi-personal-income
              id="${this.childIds.additionalIncome}"
              additional-amount
              is-iconic
              @validity="${this.updateValidity}"
              ?is-primary="${this.isPrimary}"
              helper-text="You don’t have to include alimony, child support, 
                or separate maintenance income unless you want to use it to repay your loan."
            ></refi-personal-income>`
          : nothing}

        <div class="button-container-iconic">
          <fs-button class="fs-button-iconic next-button" variant="primary"
            >Submit your income</fs-button
          >
        </div>
      </form>
    `;
  }

  toggleAdditionalIncome(ev) {
    ev.preventDefault();
    this.showAdditionalIncome = !this.showAdditionalIncome;
    delete this.validity.incomeAmountOther;
  }

  employmentChange(ev) {
    this.employmentStatus = ev.target.value;
    stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
      replacements: {
        value: 'iconic-employmentStatus',
      },
    });
    this.employedOrSelfEmployed =
      this.employmentStatus ===
        REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.EMPLOYED ||
      this.employmentStatus ===
        REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.SELF_EMPLOYED;
    this.employmentMissing = false;
  }

  updateValidity(ev) {
    this.validity[ev.detail.name] = ev.detail.isValid;
  }

  @validateForm()
  async onSubmit() {
    this.employmentMissing =
      this.employmentStatus === null || this.employmentStatus === '';
    this.updateValidity({
      detail: { name: 'employment', isValid: !this.employmentMissing },
    });
    this.requestUpdate();
    let valid = true;
    this.debug.log(this.validity);
    Object.values(this.validity).forEach((validityValue) => {
      if (!validityValue) {
        valid = false;
      }
    });
    if (valid) {
      this.oldApplicantInfo = JSON.parse(JSON.stringify(this.applicant));
      this.oldApplicantEmploymentInfo = JSON.parse(
        JSON.stringify(this.applicant.employmentInfo)
      );

      // validate and save income info
      this.inputChildren = {
        job: this.shadowRoot.getElementById(this.childIds.job),
        income: this.shadowRoot.getElementById(this.childIds.income),
        additionalIncome: this.shadowRoot.getElementById(
          this.childIds.additionalIncome
        ),
        employmentActiveToggle: this.shadowRoot.getElementById(
          this.childIds.employmentActiveToggle
        ),
      };

      this.applicant.employmentStatus = this.employmentStatus;
      Object.values(this.inputChildren).forEach((child) => {
        if (child) {
          Object.keys(child.values).forEach((field) => {
            if (
              field === 'jobDescription' ||
              field === 'activeIncomeVerification'
            ) {
              this.applicant.employmentInfo[field] = child.values[field];
            } else if (field === 'incomeAmount' || field === 'otherIncome') {
              this.applicant[field] = child.values[field];
            }
          });
        }
      });
      if (!this.showAdditionalIncome) {
        this.applicant.otherIncome = null;
      }
      this.completeEmploymentInfo();

      // income tags
      stratum.publishTag(TagKey.PERSONAL_INCOME_ADD_ADDITIONAL_INCOME, {
        replacements: { value: this.showAdditionalIncome.toString() },
      });

      // employment tags
      stratum.publishTag(TagKey.PERSONAL_EMPLOYMENT_EMPLOYMENT_STATUS, {
        replacements: { value: this.employmentStatus },
      });
      stratum.publishTag(TagKey.PERSONAL_EMPLOYMENT_PFCI_QUESTION, {
        replacements: {
          value: this.applicant.employmentInfo.activeIncomeVerification,
        },
      });

      this.tagFieldChangesOnSubmit();

      this.debug.log('valid', this.application);
      this.dispatchEvent(new CustomEvent('next-step'));
    }
  }

  completeEmploymentInfo() {
    // save employment info
    this.applicant.employmentInfo.employerName = this.employmentStatus;
    if (!this.employedOrSelfEmployed) {
      this.applicant.employmentInfo.activeIncomeVerification = null;
    }
    if (
      this.applicant.employmentStatus === 'employed' ||
      this.applicant.employmentStatus === 'selfemployed' ||
      this.applicant.employmentStatus === 'other'
    ) {
      this.applicant.employmentInfo.yearsEmployed = '0';
      this.applicant.employmentInfo.monthsEmployed = '1';
    }
    if (this.applicant.employmentStatus === 'retired') {
      this.applicant.employmentInfo.yearsEmployed = '0';
      this.applicant.employmentInfo.monthsEmployed = '0';
      this.applicant.employmentInfo.jobDescription = 'Retired';
    }
    if (this.applicant.employmentStatus === 'unemployed') {
      this.applicant.employmentInfo.yearsEmployed = null;
      this.applicant.employmentInfo.monthsEmployed = null;
      this.applicant.employmentInfo.jobDescription = 'Unemployed';
    }
    if (this.applicant.employmentStatus === 'other') {
      this.applicant.employmentInfo.jobDescription = 'Other';
    }
  }

  tagFieldChangesOnSubmit() {
    Object.keys(this.applicant).forEach((field) => {
      if (
        typeof this.applicant[field] !== 'object' &&
        this.applicant[field] !== this.oldApplicantInfo[field]
      ) {
        this.debug.log(`${this.isPrimary ? '' : 'co-'}${field}`);
        stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_CHANGED_FIELD, {
          replacements: {
            value: `${this.isPrimary ? '' : 'co-'}${field}`,
          },
        });
      }
    });
    Object.keys(this.applicant.employmentInfo).forEach((field) => {
      if (
        this.applicant.employmentInfo[field] !==
        this.oldApplicantEmploymentInfo[field]
      ) {
        this.debug.log(`${this.isPrimary ? '' : 'co-'}${field}`);
        stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_CHANGED_FIELD, {
          replacements: {
            value: `${this.isPrimary ? '' : 'co-'}${field}`,
          },
        });
      }
    });
  }
}

customElements.define('refi-step-iconic-income', RefiStepIconicIncomeComponent);
